import { DetailsHeader, Segments } from "components";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router";
import PeopleActivityReport from "./Activity/PeopleActivityReport";
import UsersReport from "./Users/PeopleUsersReport";
import PeopleOverviewReport from "./Overview/PeopleOverviewReport";
import { useCallback, useRef } from "react";
import AcquisitionReport from "./Acquisition/PeopleAcquisitionReport";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import cn from "classnames";
import { Dropdown } from "semantic-ui-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";

import styles from "../Content/contentOverviewReports.module.scss";
import { RouteNames } from "enums";

const pages = [
  { to: "overview", label: "Overview" },
  { to: "activity", label: "Activity" },
  { to: "acquisition", label: "Acquisition" },
  { to: "users", label: "Users" },
];

type OwnProps = {
  usesDropdownMenuNavigation?: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
};

export const ReportingPeople = ({ usesDropdownMenuNavigation, childAccountId, hasCreateAccountsAddOn }: OwnProps) => {
  const isPeopleReportingEnabled = !!useFeatureFlag(FeatureFlags.PeopleReports);
  const rootRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToTop = useCallback(() => {
    rootRef.current?.scrollTo({ top: 0, left: 0 });
  }, []);

  const renderDropdownMenuNavigation = useCallback(() => {
    const currentPage = pages.find((p) => location.pathname.includes(`/${p.to}`)) ?? pages[0];

    return (
      <Dropdown
        className={styles.overviewDropdown}
        text={currentPage.label}
        icon={<FontAwesomeIcon className={styles.icon} icon={faChevronDown} />}
        floating
      >
        <Dropdown.Menu>
          {pages.map((page) => (
            <Dropdown.Item
              key={page.to}
              active={currentPage.to === page.to}
              onClick={() => {
                navigate(`../${page.to}`, { relative: "path" });
              }}
              text={page.label}
            />
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  }, [location.pathname, navigate]);

  const renderHeader = useCallback(() => {
    if (usesDropdownMenuNavigation) {
      return renderDropdownMenuNavigation();
    }
    return null;
  }, [usesDropdownMenuNavigation, renderDropdownMenuNavigation]);

  if (!isPeopleReportingEnabled) {
    return <ReportUnavailable />;
  }

  const renderTabNavigation = (
    <>
      <DetailsHeader title="People" showReportLastUpdated />
      <Segments to="/reporting/people">
        {pages.map((page) => (
          <Segments.Segment key={page.to} {...page} />
        ))}
      </Segments>
    </>
  );

  return (
    <div
      className={cn("people-reporting-root", {
        "nested-content": !usesDropdownMenuNavigation,
        [styles.rootDropdownMenuNavigation]: usesDropdownMenuNavigation,
      })}
      ref={rootRef}
    >
      {!usesDropdownMenuNavigation && renderTabNavigation}
      <div className="reportBody">
        <Routes>
          <Route
            path="overview"
            element={
              <PeopleOverviewReport
                scrollToTop={scrollToTop}
                usesDropdownMenuNavigation={usesDropdownMenuNavigation}
                renderHeader={renderHeader}
                childAccountId={childAccountId}
                hasCreateAccountsAddOn={hasCreateAccountsAddOn}
              />
            }
          />
          <Route
            path="activity"
            element={
              <PeopleActivityReport
                usesDropdownMenuNavigation={usesDropdownMenuNavigation}
                renderHeader={renderHeader}
                childAccountId={childAccountId}
                hasCreateAccountsAddOn={hasCreateAccountsAddOn}
              />
            }
          />
          <Route
            path="acquisition"
            element={
              <AcquisitionReport
                usesDropdownMenuNavigation={usesDropdownMenuNavigation}
                renderHeader={renderHeader}
                childAccountId={childAccountId}
                hasCreateAccountsAddOn={hasCreateAccountsAddOn}
              />
            }
          />
          <Route
            path="users"
            element={
              <UsersReport
                usesDropdownMenuNavigation={usesDropdownMenuNavigation}
                renderHeader={renderHeader}
                childAccountId={childAccountId}
                hasCreateAccountsAddOn={hasCreateAccountsAddOn}
              />
            }
          />
          <Route
            path="*"
            element={
              <Navigate
                to={usesDropdownMenuNavigation ? "../overview" : `/${RouteNames.reporting}/people/overview`}
                replace
              />
            }
          />
        </Routes>
      </div>
    </div>
  );
};
